import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

import Vue from 'vue'

export interface HttpOptions {
  baseURL?: string; // /api
  withCredentials?: boolean; // false
  timeout?: number; // 30 * 1000
  successCode?: string | number; // 200
  expiredCode?: string | number; // 401
  expiredHandler?: (url: string) => void; // 失效处理函数
  requestHandler?: (cfg: AxiosRequestConfig) => void;
  excludeCodes?: Array<string | number>;
}

function stringTrim(obj: any) {
  const type = Object.prototype.toString.call(obj)
  if (type === '[object Object]') {
    for (const i in obj) {
      obj[i] = stringTrim(obj[i])
    }
  } else if (type === '[object Array]') {
    for (let i = 0, len = obj.length; i < len; i++) {
      obj[i] = stringTrim(obj[i])
    }
  } else if (type === '[object String]') {
    obj = obj.trim()
  }
  return obj
}

export default class Http {
  inst: AxiosInstance

  constructor (options: HttpOptions = {}) {
    this.inst = axios.create({
      baseURL: Vue.prototype.$qiankun ? '/api-base-contract-fe' : '/',
      withCredentials: !!options.withCredentials,
      timeout: options.timeout || 1000 * 60
    })
    this.addRequestInterceptor(options)
    this.addResponseInterceptor(options)
  }

  private addRequestInterceptor (options: HttpOptions) {
    this.inst.interceptors.request.use(req)

    function req (cfg: AxiosRequestConfig): any {
      if (options.requestHandler) options.requestHandler(cfg)
      return cfg
    }
  }

  private addResponseInterceptor (options: HttpOptions) {
    const vm = this as any
    this.inst.interceptors.response.use(res, resErr)

    function res (response: AxiosResponse): any {
      if (response.status === 200) {
        if (Object.prototype.toString.call(response.data) !== '[object Object]') {
          if (response.data?.type === 'application/json') {
            const fr = new FileReader()
            const p = new Promise((resolve, reject) => {
              fr.readAsText(response.data)
              fr.onload = function (e) {
                const jsonData = JSON.parse(fr.result as any)
                jsonHandler({ data: jsonData }).then((data) => resolve(data))
                  .catch((e: any) => reject(e))
              }
            })
            return p
          } else {
            return Promise.resolve(response)
          }
        }
        return jsonHandler(response)
      } else {
        vm.message(response.statusText)
        return Promise.reject(response)
      }

      function jsonHandler (response: any) {
        const { code, message, data } = response.data
        if (options.excludeCodes && options.excludeCodes.includes(code)) return Promise.resolve(response)
        if (+code === +(options.successCode || 200)) return Promise.resolve(data)
        else if (+code === +(options.expiredCode || 401)) {
          if (options.expiredHandler) {
            options.expiredHandler(data)
          } else if (Vue.prototype.$auth) {
            Vue.prototype.$auth.toLogin(data, false)
          } else vm.message(message)
          return Promise.reject(response)
        } else {
          vm.message(message)
          return Promise.reject(response)
        }
      }
    }

    function resErr (error: any) {
      vm.message(error)
      return Promise.reject(error)
    }
  }

  private message (msg: string) {
    if (Vue.prototype.$notify) {
      Vue.prototype.$notify({
        title: '警告',
        message: msg,
        dangerouslyUseHTMLString: true,
        type: 'warning'
      })
    } else {
      alert(Text)
    }
  }

  getUri (config?: AxiosRequestConfig): string {
    return this.inst.getUri(config)
  }

  request<T = any> (config: AxiosRequestConfig): Promise<T> {
    return this.inst.request(config)
  }

  get<T = any> (url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.inst.get(url, config)
  }

  delete<T = any> (url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.inst.delete(url, config)
  }

  head<T = any> (url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.inst.head(url, config)
  }

  post<T = any> (url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.inst.post(url, stringTrim(data), config)
  }

  put<T = any> (url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.inst.put(url, stringTrim(data), config)
  }

  patch<T = any> (url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.inst.patch(url, stringTrim(data), config)
  }
}
